/**
 * @file src/offline/offlineIntent.js
 * @description The one thing root can ask this app to do on arrival: download the collection.
 *
 * Root's collection page has a "Download for offline" action, and root cannot download anything
 * itself — the store is on THIS origin. So the action opens MultiStage with `?offline=download`,
 * and the studio starts the download once the collection is on screen. Progress, removal and the
 * list stay here, where the bytes are.
 *
 * The intent is consumed, not kept. It is removed from the address as soon as it has been acted on,
 * so a reload does not start the download twice, and it is not in the stored entry URL's allowlist
 * (`offlineEntryUrl.js`), so a cold offline boot never sees it.
 */

import { ensureParams } from '../utils/urlParams.js';
import { DEFAULT_OFFLINE_QUALITY, normalizeOfflineQuality } from './offlineConfig.js';

export const OFFLINE_INTENT_PARAM = 'offline';
export const OFFLINE_INTENT_DOWNLOAD = 'download';
export const OFFLINE_QUALITY_PARAM = 'quality';

/**
 * Whether this address asks for a download.
 * @param {URLSearchParams} [params] defaults to the current location
 * @returns {boolean}
 */
export function hasOfflineDownloadIntent(params) {
  return ensureParams(params).get(OFFLINE_INTENT_PARAM) === OFFLINE_INTENT_DOWNLOAD;
}

/**
 * Which quality the intent asks for.
 *
 * Root offers the choice because that is where somebody decides to keep a collection, and asking
 * there is what stops a lossless download from transferring a compressed copy first. The param is
 * advisory in exactly one sense: this app owns the store, so it is this app that measures the
 * objects and refuses if they will not fit. An absent or unrecognised value is compressed — the
 * cheap answer is the one a malformed address gets.
 *
 * @param {URLSearchParams} [params] defaults to the current location
 * @returns {'compressed'|'lossless'}
 */
export function offlineDownloadQuality(params) {
  const raw = ensureParams(params).get(OFFLINE_QUALITY_PARAM);
  return raw ? normalizeOfflineQuality(raw) : DEFAULT_OFFLINE_QUALITY;
}

/**
 * Remove the intent from the current address without a navigation, so it cannot fire again.
 * A no-op outside a browser or when the param is absent.
 */
export function clearOfflineDownloadIntent() {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(OFFLINE_INTENT_PARAM) !== OFFLINE_INTENT_DOWNLOAD) return;
    url.searchParams.delete(OFFLINE_INTENT_PARAM);
    // The quality travels with the intent and is consumed with it. Left behind, a reload would read
    // it as a fresh request to switch a collection somebody has since switched back.
    url.searchParams.delete(OFFLINE_QUALITY_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Leaving the param in place only risks a second download prompt on reload; nothing breaks.
  }
}
